const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DISPLAY_DATE = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const DISPLAY_TIME_ZONE = 'America/Sao_Paulo';

function isCalendarDate(year: string, month: string, day: string): boolean {
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  return date.toISOString().slice(0, 10) === `${year}-${month}-${day}`;
}

/** Formata data `YYYY-MM-DD` como `dd/MM/yyyy` sem conversão de fuso. */
export function formatDate(isoDate: string): string {
  const match = ISO_DATE.exec(isoDate);
  if (!match) return isoDate;
  const [, year = '', month = '', day = ''] = match;
  return `${day}/${month}/${year}`;
}

/** Converte `dd/MM/yyyy` em `YYYY-MM-DD`, validando o calendário; `null` se inválida. */
export function parseDisplayDate(value: string): string | null {
  const match = DISPLAY_DATE.exec(value.trim());
  if (!match) return null;
  const [, day = '', month = '', year = ''] = match;
  return isCalendarDate(year, month, day) ? `${year}-${month}-${day}` : null;
}

/** Data de hoje (`YYYY-MM-DD`) no fuso de negócio, America/Sao_Paulo. */
export function todayInBusinessTimeZone(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: DISPLAY_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/** Formata um instante ISO como data local em America/Sao_Paulo. */
export function formatInstantDate(isoInstant: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: DISPLAY_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(isoInstant));
}

/** Formata um instante ISO como data e hora (`dd/MM/yyyy, HH:mm`) em America/Sao_Paulo. */
export function formatInstantDateTime(isoInstant: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: DISPLAY_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(isoInstant));
}

const YEAR_MONTH = /^(\d{4})-(\d{2})$/;
const MONTH_ABBREVIATIONS = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
] as const;

/** Formata mês `YYYY-MM` como `set/2026`; fora do formato devolve o valor recebido. */
export function formatYearMonth(yearMonth: string): string {
  const match = YEAR_MONTH.exec(yearMonth);
  const abbreviation = match === null ? undefined : MONTH_ABBREVIATIONS[Number(match[2]) - 1];
  return match === null || abbreviation === undefined
    ? yearMonth
    : `${abbreviation}/${match[1] ?? ''}`;
}

/** Mês corrente (`YYYY-MM`) em America/Sao_Paulo. */
export function currentYearMonth(now: Date = new Date()): string {
  return todayInBusinessTimeZone(now).slice(0, 7);
}

/** Mês `YYYY-MM` deslocado em `months` meses (negativo recua). */
export function addYearMonths(yearMonth: string, months: number): string {
  const year = Number(yearMonth.slice(0, 4));
  const month = Number(yearMonth.slice(5, 7));
  const index = year * 12 + (month - 1) + months;
  return `${String(Math.floor(index / 12)).padStart(4, '0')}-${String((index % 12) + 1).padStart(2, '0')}`;
}
